import { Checkbox, InputNumber, Radio, Select } from "antd";
import type { MarkupDraft } from "../lib/quoteMarkup";

/**
 * Markup type, value and optional GST inputs for an agent's private quote markup.
 * @param draft Current values
 * @param currency Quote currency, shown beside a fixed amount
 * @param onChange Receives the full updated values
 */
export const QuoteMarkupFields = ({
  draft,
  currency,
  onChange,
  idPrefix = "quote-markup",
}: {
  draft: MarkupDraft;
  currency: string;
  onChange: (draft: MarkupDraft) => void;
  idPrefix?: string;
}) => (
  <div className="space-y-3">
    <Select aria-label="Agent markup basis" placeholder="Choose markup basis" value={draft.markupBasis} onChange={(markupBasis) => onChange({ ...draft, markupBasis })} options={[{ value: "taxExclusive", label: "Supplier selling price before supplier taxes" }, { value: "taxInclusive", label: "Supplier selling price including supplier taxes" }]} className="w-full" />
    <Select aria-label="Agent tax base" placeholder="Choose agent tax base" value={draft.taxBase} onChange={(taxBase) => onChange({ ...draft, taxBase })} options={[{ value: "resale", label: "Supplier price before taxes plus agent markup" }, { value: "markup", label: "Agent markup only" }, { value: "taxInclusiveResale", label: "Supplier price including taxes plus agent markup" }]} className="w-full" />
    <div className="flex flex-wrap items-center gap-3">
      <Radio.Group
        value={draft.markupType}
        onChange={(event) => onChange({ ...draft, markupType: event.target.value })}
        aria-label="Markup type"
      >
        <Radio value="percentage">Percentage</Radio>
        <Radio value="fixed">Fixed amount</Radio>
      </Radio.Group>
      <InputNumber
        id={`${idPrefix}-value`}
        min={0}
        max={draft.markupType === "percentage" ? 100 : 100000000}
        value={draft.markupValue}
        onChange={(value) => onChange({ ...draft, markupValue: value })}
        aria-label="Markup value"
        addonAfter={draft.markupType === "percentage" ? "%" : currency}
        className="!w-40"
      />
    </div>
    <div className="flex flex-wrap items-center gap-3">
      <Checkbox checked={draft.gstEnabled} onChange={(event) => onChange({ ...draft, gstEnabled: event.target.checked })}>
        Add GST on the selected agent tax base
      </Checkbox>
      {draft.gstEnabled ? (
        <InputNumber
          min={0}
          max={40}
          value={draft.gstPercent}
          onChange={(value) => onChange({ ...draft, gstPercent: value ?? 0 })}
          aria-label="GST percentage"
          addonAfter="%"
          className="!w-28"
        />
      ) : null}
    </div>
  </div>
);
